/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Segmented } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import { useRegistrationSetupContext } from '../../../context'

/**
 * Competition-wide refund configuration. Lives on the Registration Setup →
 * Payments tab, below Processing Fees — these settings are never per-division.
 */
const Refund = () => {
  'use memo'

  const { state, setState } = useRegistrationSetupContext()

  // The wire key is a boolean (`refund_payment_processing_fee`) where true
  // means the league absorbs the fee. The picker speaks in bearers to match
  // the Processing Fees control above, so map between the two here.
  const bearer = state.refundPaymentProcessingFee ? 'league' : 'registrant'

  return (
    <div css={sectionStyle}>
      {/* Matches the "Processing Fees" heading above: same variant and weight,
          and no explicit color so it inherits the same default. */}
      <Text variant='captionLarge' weight='semiBold'>
        {capitalizeWords('Refund Processing Fees').toUpperCase()}
      </Text>

      <div css={groupStyle}>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          {capitalizeWords('Who Pays The Refund Processing Fee?')}
        </Text>
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          When a registration is refunded, your payment provider keeps the
          processing fee it already charged. Choose whether your league covers
          it so the registrant is made whole, or the registrant receives their
          payment minus that fee.
        </Text>

        <Segmented
          css={segmentedToggleStyle}
          value={bearer}
          onChange={(value) => {
            setState((prev) => ({
              ...prev,
              refundPaymentProcessingFee: value === 'league',
            }))
          }}
          options={[
            { label: capitalizeWords('League Absorbs It'), value: 'league' },
            {
              label: capitalizeWords('Registrant Pays It'),
              value: 'registrant',
            },
          ]}
        />
      </div>
    </div>
  )
}

export default Refund

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  width: 100%;
  /* Last element on the Payments tab, so it would otherwise sit flush against
     the bottom of the scroll container — cramped on short viewports. */
  padding-bottom: 80px;
`

const groupStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`

// Antd v5's default Segmented palette doesn't fit this dark surface. Matches the
// Processing Fees control above so the two read as the same kind of choice.
const segmentedToggleStyle = css`
  align-self: flex-start;
  &.ant-segmented {
    background-color: ${colors.SECONDARY_200};
    padding: 3px;
    border-radius: 8px;
  }
  .ant-segmented-item {
    color: ${colors.NEUTRAL_30};
    transition: color 0.15s;
    border-radius: 6px;
  }
  .ant-segmented-item:hover:not(.ant-segmented-item-disabled) {
    color: ${colors.WHITE};
  }
  .ant-segmented-item-selected {
    background-color: ${colors.PRIMARY} !important;
    color: ${colors.SECONDARY_300} !important;
    font-weight: 600;
  }
  .ant-segmented-item-label {
    line-height: 34px;
    padding: 0 20px;
  }
`
